import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "427",
  "slug": "geography-activities-first-week-of-school",
  "title": "Geography Activities for the First Week of School",
  "excerpt": "Low-prep, high-energy geography activities to open the year, break the ice, and get a new class reading maps together from day one.",
  "date": "2026-06-13",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1577896851231-70ef18881754?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxzdHVkZW50cyUyMGNsYXNzcm9vbSUyMGxlYXJuaW5nfGVufDF8MHx8fDE3ODEzMzg1NTF8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "3 min",
  "author": "EarthGuessr Team",
  "tldr": "The first week of school is the ideal time for low-stakes geography activities that double as icebreakers, from a where-am-I satellite guessing game to a class map of student origins. They build community while quietly assessing what students already know.",
  "content": [
    {
      "type": "paragraph",
      "text": "The opening week of a new school year is a strange, valuable window. Routines are not set, grades feel low-stakes, and a whole class of students is sizing up the room and each other. Geography is a perfect subject for this moment, because maps give everyone something to point at, argue about, and discover together without anyone needing to have done the reading."
    },
    {
      "type": "heading",
      "text": "Why Lead With Geography"
    },
    {
      "type": "paragraph",
      "text": "Use the first activity to learn students' names and see how they approach an unfamiliar map. Collect a brief individual answer before a group discussion so the most confident speakers do not stand in for the whole class."
    },
    {
      "type": "heading",
      "text": "The Where-Am-I Satellite Game"
    },
    {
      "type": "paragraph",
      "text": "Project a satellite image of an interesting but not impossible location and ask the class to reason their way to it. Encourage them to talk through the clues out loud: Which hemisphere, based on the vegetation? Coastal or inland? Desert, forest, or farmland? Let them argue, narrow it down, then reveal the answer and walk back through the reasoning that would have got them there."
    },
    {
      "type": "paragraph",
      "text": "Begin with an observable feature such as a river or a coastline. Ask students to point to the evidence they used, and let them revise an answer after hearing another interpretation."
    },
    {
      "type": "heading",
      "text": "The Class Origin Map"
    },
    {
      "type": "paragraph",
      "text": "Pin a large world map to the wall and give every student a marker or sticky note. Ask them to mark where they were born, where their family is from, or the furthest place they have ever travelled. Within minutes you have a personalised classroom map that becomes a reference point for the rest of the year."
    },
    {
      "type": "paragraph",
      "text": "It is an icebreaker disguised as geography. Students find shared connections, the quiet ones get a low-pressure way to share something about themselves, and you get a living display you can point back to every time a relevant country comes up in a lesson. By winter, that wall map will be covered in stories."
    },
    {
      "type": "heading",
      "text": "Quick Activities That Need Almost No Prep"
    },
    {
      "type": "list",
      "items": [
        "Geography speed round: read five rapid map questions and have students answer on mini whiteboards. Fast, loud, and instantly diagnostic.",
        "Continents and oceans sketch: students draw the world from memory in two minutes, then compare with a real map. The gap between the two is the lesson.",
        "Find your partner: hand out cards with a country on some and its capital on others, and have students move around to find their match.",
        "Two truths and a place: students share two real facts and one invented one about a country they like, and the class guesses the lie."
      ]
    },
    {
      "type": "heading",
      "text": "Building In a Little Assessment"
    },
    {
      "type": "paragraph",
      "text": "None of this has to feel like testing, but you can quietly gather a lot. As students play the where-am-I game or sketch the continents, jot notes on who reaches for cardinal directions naturally, who confuses continents with countries, and who lights up when a map appears. That snapshot of prior knowledge is worth more than a diagnostic quiz, because it is collected while students are relaxed and engaged rather than anxious."
    },
    {
      "type": "heading",
      "text": "Set the Tone for the Year"
    },
    {
      "type": "paragraph",
      "text": "Keep a few of the initial responses and return to a similar task later in the term. Compare the explanations using the same criteria to decide which concepts need more practice."
    }
  ]
};
